/**
 * The question, in the transcript. One block, three things it can be.
 *
 * A `question` block is in the turn from the moment `onRequest` lands, and it stays there after the
 * answer — so this component covers the whole life of one ask rather than only the part that is
 * interactive:
 *
 * - **asking, inline route** — the whole widget. `ActivityBlock`'s `question` body: the sentence at
 *   14/22 over a wrapping row of `AnswerChip`s, under the one header in the column with neither a
 *   meta nor a chevron. That is `AgentTurn`'s *Asking back* board, unchanged.
 * - **asking, wizard route** — the beat only. The card is in the composer, so the transcript says
 *   what is happening and nothing more; drawing the whole thing here would be the same five
 *   questions on screen twice.
 * - **answered or skipped** — the receipt. What was asked, what was said, and which ones were
 *   passed over.
 *
 * It never decides *whether* the composer is taken over — `routeOf` does, and `DeckAgentPane` reads
 * the same function. One rule, two readers, no way for them to disagree.
 */
import { useState } from 'react';
import {
  ActivityBlock,
  ActivityGlyph,
  ActivityList,
  ActivityRow,
  type ChatTheme,
} from '~/design-system/components/v1_5/Chat';
import { cn } from '~/lib/utils';
import type { TurnBlock } from '../turnModel';
import { useQuestionAnswer } from './answerContext';
import styles from './QuestionBlock.module.css';
import { answerInline, answeredRows, questionHeading, routeOf } from './questionModel';

export type QuestionTurnBlock = Extract<TurnBlock, { kind: 'question' }>;

export interface QuestionBlockProps {
  block: QuestionTurnBlock;
  theme: ChatTheme;
}

export function QuestionBlock({ block, theme }: QuestionBlockProps) {
  const api = useQuestionAnswer();
  /**
   * Which chip was just pressed.
   *
   * Local, and deliberately not lifted: between the tap and the resume stream's first paint the
   * block still says `asking`, and a chip that does not change at all reads as a click that missed.
   * It is discarded the moment the block comes back as `answered`, which is the only outcome.
   */
  const [picked, setPicked] = useState<string | null>(null);

  const { items, status } = block;

  if (status !== 'asking') {
    return <Answered items={items} skipped={status === 'skipped'} theme={theme} />;
  }

  const route = routeOf(items);
  // A request with nothing in it draws nothing. It can arrive from a malformed tool call, and an
  // empty widget in the transcript is worse than a turn that quietly carries on.
  if (route === 'none') return null;

  const heading = questionHeading(items.length);

  if (route === 'wizard') {
    return (
      <ActivityBlock
        theme={theme}
        row={{
          density: 'open',
          icon: <ActivityGlyph tool="chat" size={16} theme={theme} />,
          label: heading,
          meta: items.length > 1 ? `${items.length} questions` : undefined,
          theme,
        }}
      />
    );
  }

  const item = items[0];
  const answering = Boolean(picked) || Boolean(api?.submitting);

  return (
    <ActivityBlock
      theme={theme}
      // No meta, no chevron, no `onToggle` — the one widget header in the column with neither, which
      // is what keeps `ActivityRow` on its `<div>` branch. Built by hand rather than through a
      // lifecycle mapper for exactly that reason.
      row={{
        density: 'open',
        icon: <ActivityGlyph tool="chat" size={16} theme={theme} />,
        label: heading,
        theme,
      }}
      body={{
        kind: 'question',
        question: item.question,
        choices: (item.options ?? []).map((option) => ({
          label: option.label,
          selected: picked === option.label,
          onSelect: () => {
            if (!api || answering) return;
            setPicked(option.label);
            api.answer('SUBMIT', answerInline(item, option.label));
          },
        })),
      }}
    />
  );
}

/**
 * A settled question: the header line, then one row per ask.
 *
 * The header is a real `ActivityRow` and the card a real `ActivityList`; only the two-run row inside
 * is local, because `ActivityListRow`'s leading run is pinned and never wraps — see the stylesheet.
 */
function Answered({
  items,
  skipped,
  theme,
}: {
  items: QuestionTurnBlock['items'];
  skipped: boolean;
  theme: ChatTheme;
}) {
  const rows = answeredRows(items);

  // `onContextSkipped` — the agent withdrawing its own question — carries no items at all. There is
  // nothing to summarise, so the turn keeps the beat and drops the card.
  if (rows.length === 0) {
    return (
      <ActivityRow
        density="open"
        icon={<ActivityGlyph tool="chat" size={16} tone="inherit" theme={theme} />}
        label={skipped ? 'Question withdrawn' : 'Asked'}
        tone="muted"
        theme={theme}
      />
    );
  }

  const answered = rows.filter((row) => !row.skipped).length;

  return (
    <div className={styles.group}>
      <ActivityRow
        density="open"
        icon={<ActivityGlyph tool="chat" size={16} theme={theme} />}
        label={rows.length > 1 ? 'Answered' : 'Asked'}
        meta={rows.length > 1 ? `${answered} of ${rows.length}` : undefined}
        // The card below is the block's own body, so the row does not also announce its label.
        announce={false}
        theme={theme}
      />
      <ActivityList theme={theme} announce={false} label="Answers">
        {rows.map((row, index) => (
          // Two questions in one request can legitimately read the same, so the index is the only
          // stable identity a row has.
          <li key={index} className={styles.row}>
            <span className={styles.question}>{row.question}</span>
            <span className={cn(styles.answer, row.skipped && styles.skipped)}>{row.answer}</span>
          </li>
        ))}
      </ActivityList>
    </div>
  );
}
